/**
 * CreditStatementsTable
 * Displays the list of monthly account statements for a credit (P7).
 * Each row exposes a "Ver PDF" button that opens an inline viewer modal.
 */

import { Eye, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import type { CreditStatementListItem } from '@/types/credit.types';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

const STATUS_LABEL: Record<CreditStatementListItem['status'], string> = {
  PENDING: 'Pendiente',
  GENERATED: 'Generado',
  ERROR: 'Error',
};

const STATUS_VARIANT: Record<CreditStatementListItem['status'], BadgeVariant> = {
  PENDING: 'warning',
  GENERATED: 'success',
  ERROR: 'danger',
};

interface Props {
  statements: CreditStatementListItem[];
  isLoading: boolean;
  isError: boolean;
  viewingId: string | null;
  onView: (item: CreditStatementListItem) => void;
  onRetry: () => void;
}

function formatGeneratedAt(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function CreditStatementsTable({
  statements,
  isLoading,
  isError,
  viewingId,
  onView,
  onRetry,
}: Props) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton
            key={`statement-skeleton-${i.toString()}`}
            variant="rectangular"
            height={48}
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-sm text-muted-foreground">
        <span>Error al cargar los estados de cuenta.</span>
        <Button onClick={onRetry} size="sm">
          <RefreshCw size={14} />
          Reintentar
        </Button>
      </div>
    );
  }

  if (statements.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Sin estados de cuenta generados todavía.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Período</th>
            <th className="px-3 py-2 text-left font-medium">Estado</th>
            <th className="px-3 py-2 text-left font-medium">Generado</th>
            <th className="px-3 py-2 text-right font-medium">Acción</th>
          </tr>
        </thead>
        <tbody>
          {statements.map((s) => {
            const isOpening = viewingId === s.statement_id;
            const canView = s.status === 'GENERATED';
            return (
              <tr key={s.statement_id} className="border-t border-border">
                <td className="px-3 py-2 font-medium text-foreground">{s.period}</td>
                <td className="px-3 py-2">
                  <Badge variant={STATUS_VARIANT[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {formatGeneratedAt(s.generated_at)}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!canView || isOpening}
                    onClick={() => onView(s)}
                  >
                    <Eye size={14} />
                    {isOpening ? 'Abriendo...' : 'Ver PDF'}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
